import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import "./LandingPage.css";
import landingLogo from "@/assets/bubble-radar-landing-logo.png";
import dashboardImage from "@/assets/bubble-radar-dashboard.png";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PUBLIC_SITE_URL = process.env.REACT_APP_PUBLIC_SITE_URL;

const HERO_METRICS = [
  { value: "LPPLS + TA", label: "Unified signal stack" },
  { value: "0–100%", label: "Crash‑risk confidence" },
  { value: "30–480", label: "Timeframe coverage" },
];

const HERO_SIGNALS = [
  {
    title: "Risk Bands",
    tag: "0‑20 Buy → 81‑100 Crash",
    value: "Confidence: 68%",
  },
  {
    title: "LPPL time‑to‑TC",
    tag: "Shorter = higher risk",
    value: "14 days",
  },
  {
    title: "Signal Stack",
    tag: "RSI + CMF + MACD",
    value: "Aligned",
  },
  {
    title: "LPPL Confidence",
    tag: "Fit reliability",
    value: "0.63",
  },
];

const INDICATORS = [
  {
    chip: "TC Date",
    title: "Critical Time Window",
    text: "Pinpoint when the model expects a peak or break to occur. Closer dates imply higher risk.",
  },
  {
    chip: "Timeframe",
    title: "Short / Medium / Long",
    text: "30 days = short‑term, 60/120 = medium‑term, 240 = long‑term.",
  },
  {
    chip: "Custom Alerts",
    title: "Custom E‑mail Alerts",
    text: "Set personalized thresholds and get notified the moment a risk score or confidence band shifts.",
  },
  {
    chip: "Artificial Intelligence",
    title: "AI Confluence Check",
    text: "Use AI technology to confirm or deny signals based on traditional indicators for extra confluence.",
  },
];

const FLOW_STEPS = [
  {
    title: "1. Filter by asset",
    text: "Search tickers and apply category tabs across crypto, equities, and metals.",
  },
  {
    title: "2. Check AI Analysis and upcoming high risk bubble dates",
    text: "Use AI analysis to review where high-risk bubble dates are approaching.",
  },
  {
    title: "3. Check Market Risk Window for additional confirmations",
    text: "Cross-check the risk window before deciding whether to buy, sell, or wait.",
  },
];

const PRICING_EXPLORER = [
  "Access to all indicators under Explorer package.",
  "6 asset watchlists",
  "Email alerts",
  "Exportable charts",
];

const PRICING_CUSTOM = [
  "Unlimited assets + indicators",
  "Real‑time risk dashboard",
  "Webhook + Slack alerts",
  "Dedicated calibration support",
];

const FAQ_ITEMS = [
  {
    title: "What markets are supported?",
    text: "We cover crypto, U.S. equities, and major indices with configurable lookbacks.",
  },
  {
    title: "Does the trial include all features?",
    text: "Yes — full indicator access, alerts, and exports with no limitations.",
  },
  {
    title: "How fresh is the data?",
    text: "Signals update every day. The solution is not designed for smaller timeframes.",
  },
  {
    title: "Can I cancel anytime?",
    text: "Absolutely. Cancel within 14 days and you won’t be charged.",
  },
];

const TICKERS = ["BTC", "ETH", "NVDA", "SPX", "GME", "SOL"];

export default function LandingPage() {
  useEffect(() => {
    document.title = "Bubble Radar — 14‑Day Free Trial";
    const revealables = document.querySelectorAll(".landing-reveal");
    if (!revealables.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("show");
          }
        });
      },
      { threshold: 0.15 }
    );

    revealables.forEach((el, index) => {
      el.style.transitionDelay = `${index * 80}ms`;
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    company: "",
    team_size: "1-5",
    message: "",
  });
  const [contactStatus, setContactStatus] = useState(null);
  const [contactLoading, setContactLoading] = useState(false);

  const handleContactChange = (field) => (event) => {
    setContactForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    setContactLoading(true);
    setContactStatus(null);
    try {
      await axios.post(`${API}/contact`, contactForm);
      const message = "Thanks! We’ll reach out within 1 business day.";
      toast.success(message);
      setContactStatus({ type: "success", message });
      setContactForm({
        name: "",
        email: "",
        company: "",
        team_size: "1-5",
        message: "",
      });
    } catch (error) {
      const detail = error?.response?.data?.detail || "Unable to send your request. Please try again.";
      toast.error(detail);
      setContactStatus({ type: "error", message: detail });
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <div className="landing-page" data-testid="landing-page">
      <div className="page">
        <div className="hero-glow" aria-hidden="true" />
        <header data-testid="landing-header">
          <a
            className="logo"
            href={`${PUBLIC_SITE_URL}/`}
            aria-label="Bubble Radar home"
            data-testid="landing-logo-link"
          >
            <img
              className="logo-image"
              src={landingLogo}
              alt="Bubble Radar logo"
              data-testid="landing-logo"
            />
          </a>
          <nav data-testid="landing-nav">
            <a href={`${PUBLIC_SITE_URL}/#indicators`} data-testid="landing-nav-indicators">Indicators</a>
            <a href={`${PUBLIC_SITE_URL}/#showcase`} data-testid="landing-nav-dashboard">Dashboard</a>
            <a href={`${PUBLIC_SITE_URL}/#pricing`} data-testid="landing-nav-trial">Trial</a>
            <a href={`${PUBLIC_SITE_URL}/#faq`} data-testid="landing-nav-faq">FAQ</a>
          </nav>
          <a className="cta-btn" href={`${PUBLIC_SITE_URL}/login`} data-testid="landing-header-cta">
            Login
          </a>
        </header>

        <section className="hero" data-testid="landing-hero">
          <div>
            <h1 data-testid="landing-hero-title">
              Forget about reading complex charts. Let AI tell you the market.
            </h1>
            <p data-testid="landing-hero-subtitle">
              Track how financial bubbles build up across crypto, stocks, commodities and precious metals — with a crash‑risk
              confidence score that unifies LPPLS and traditional indicators.
            </p>
            <div className="hero-actions" data-testid="landing-hero-actions">
              <a className="btn-primary" href={`${PUBLIC_SITE_URL}/login`} data-testid="landing-hero-start-trial">
                Start 14‑day free trial
              </a>
              <a className="btn-outline" href={`${PUBLIC_SITE_URL}/#showcase`} data-testid="landing-hero-see-dashboard">
                See the dashboard
              </a>
            </div>
            <div className="hero-metrics" data-testid="landing-hero-metrics">
              {HERO_METRICS.map((metric) => (
                <div className="metric" key={metric.value} data-testid={`landing-metric-${metric.value}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}> 
                  <strong data-testid={`landing-metric-value-${metric.value}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}>{metric.value}</strong>
                  <span data-testid={`landing-metric-label-${metric.value}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}>{metric.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="hero-card" data-testid="landing-hero-card">
            <h3 data-testid="landing-hero-card-title">Crash‑Risk Confidence</h3>
            {HERO_SIGNALS.map((signal) => (
              <div className="signal" key={signal.title} data-testid={`landing-hero-signal-${signal.title}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}> 
                <div>
                  <strong data-testid={`landing-hero-signal-title-${signal.title}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}>{signal.title}</strong>
                  <div className="signal-tag" data-testid={`landing-hero-signal-tag-${signal.title}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}>{signal.tag}</div>
                </div>
                <strong data-testid={`landing-hero-signal-value-${signal.title}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}>{signal.value}</strong>
              </div>
            ))}
          </div>
        </section>

        <section id="indicators" className="section" data-testid="landing-indicators-section">
          <div className="section-title">
            <h2 data-testid="landing-indicators-title">Bubble Metrics Explanation</h2>
            <p data-testid="landing-indicators-subtitle">
              Based on available market research on LPPLS theory and function. Custom built proprietary solution based on multiple indicators.
            </p>
          </div>
          <div className="indicator-grid grid-4 landing-reveal" data-testid="landing-indicators-grid">
            {INDICATORS.map((indicator) => (
              <article className="indicator-card" key={indicator.title} data-testid={`landing-indicator-${indicator.title}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}> 
                <span className="chip" data-testid={`landing-indicator-chip-${indicator.title}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}>{indicator.chip}</span>
                <h4 data-testid={`landing-indicator-title-${indicator.title}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}>{indicator.title}</h4>
                <p data-testid={`landing-indicator-text-${indicator.title}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}>{indicator.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section" data-testid="landing-risk-section">
          <div className="section-title">
            <h2 data-testid="landing-risk-title">Crash Risk Confidence</h2>
            <p data-testid="landing-risk-subtitle">
              A blended confidence score estimating how likely the market is entering a downside / LPPL crash phase.
            </p>
          </div>
          <p style={{ textAlign: "center", fontWeight: 600, marginTop: 6 }} data-testid="landing-risk-note">
            Prepare your next move in advance
          </p>
          <div className="risk-bands" style={{ fontSize: "1.1rem" }} data-testid="landing-risk-bands">
            <span className="band-buy" data-testid="landing-risk-band-buy">0‑20 BUY</span>
            <span className="band-hold" data-testid="landing-risk-band-hold">21‑40 HOLD</span>
            <span className="band-prep" data-testid="landing-risk-band-prep">41‑60 PREPARE SELL</span>
            <span className="band-sell" data-testid="landing-risk-band-sell">61‑80 SELL</span>
            <span className="band-crash" data-testid="landing-risk-band-crash">81‑100 CRASH ZONE</span>
          </div>
        </section>

        <section id="showcase" className="section" data-testid="landing-dashboard-section">
          <div className="section-title">
            <h2 data-testid="landing-dashboard-title">Dashboard</h2>
          </div>
          <div className="gallery landing-reveal" data-testid="landing-dashboard-gallery">
            <figure className="featured" data-testid="landing-dashboard-figure">
              <img src={dashboardImage} alt="Bubble Radar dashboard demo" data-testid="landing-dashboard-image" />
              <figcaption data-testid="landing-dashboard-caption">
                <span data-testid="landing-dashboard-caption-text">Bubble Metrics Dashboard</span>
              </figcaption>
            </figure>
          </div>
        </section>

        <section className="section" data-testid="landing-flow-section">
          <div className="section-title">
            <h2 data-testid="landing-flow-title">From noise to actionable market data.</h2>
            <p data-testid="landing-flow-subtitle">
              Filter assets fast, validate model quality, and share the risk score with your team.
            </p>
          </div>
          <div className="flow landing-reveal" data-testid="landing-flow-grid">
            {FLOW_STEPS.map((step) => (
              <div className="flow-step" key={step.title} data-testid={`landing-flow-step-${step.title}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}> 
                <strong data-testid={`landing-flow-step-title-${step.title}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}>{step.title}</strong>
                <p data-testid={`landing-flow-step-text-${step.title}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}>{step.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="pricing" className="section" data-testid="landing-pricing-section">
          <div className="section-title">
            <h2 data-testid="landing-pricing-title">Start with a 14‑day free trial.</h2>
            <p data-testid="landing-pricing-subtitle">
              Full access, unlimited assets, and priority onboarding — cancel anytime before the trial ends.
            </p>
          </div>
          <div className="pricing landing-reveal" data-testid="landing-pricing-grid">
            <div className="pricing-card" data-testid="landing-pricing-explorer-card">
              <h3 data-testid="landing-pricing-explorer-title">Explorer</h3>
              <p data-testid="landing-pricing-explorer-text">Perfect for personal research and signal discovery.</p>
              <h2 data-testid="landing-pricing-explorer-price">$9.99 per month (Lock-in price before $25 per month)</h2>
              <ul data-testid="landing-pricing-explorer-list">
                {PRICING_EXPLORER.map((item) => (
                  <li key={item} data-testid={`landing-pricing-explorer-item-${item}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}>✓ {item}</li>
                ))}
              </ul>
              <a className="btn-outline" href={`${PUBLIC_SITE_URL}/login`} data-testid="landing-pricing-explorer-cta">
                Activate trial
              </a>
            </div>
            <div className="pricing-card highlight" data-testid="landing-pricing-custom-card">
              <h3 data-testid="landing-pricing-custom-title">Custom</h3>
              <p data-testid="landing-pricing-custom-text">For teams that want real‑time collaboration and advanced signal filters.</p>
              <h2 data-testid="landing-pricing-custom-price">Contact Sales</h2>
              <ul data-testid="landing-pricing-custom-list">
                {PRICING_CUSTOM.map((item) => (
                  <li key={item} data-testid={`landing-pricing-custom-item-${item}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}>✓ {item}</li>
                ))}
              </ul>
              <a className="btn-primary" href={`${PUBLIC_SITE_URL}/#contact`} data-testid="landing-pricing-custom-cta">
                Book onboarding
              </a>
            </div>
          </div>
        </section>

        <section id="contact" className="section" data-testid="landing-contact-section">
          <div className="section-title">
            <h2 data-testid="landing-contact-title">Contact sales</h2>
            <p data-testid="landing-contact-subtitle">
              Tell us about your workflow and we’ll tailor a Custom plan for your team.
            </p>
          </div>
          <div className="contact-card landing-reveal" data-testid="landing-contact-card">
            <form
              className="contact-form"
              data-testid="landing-contact-form"
              onSubmit={handleContactSubmit}
            >
              <label data-testid="landing-contact-name-label">
                Full name
                <input
                  type="text"
                  name="name"
                  placeholder="Jane Doe"
                  required
                  value={contactForm.name}
                  onChange={handleContactChange("name")}
                  data-testid="landing-contact-name-input"
                />
              </label>
              <label data-testid="landing-contact-email-label">
                Work email
                <input
                  type="email"
                  name="email"
                  placeholder="you@company.com"
                  required
                  value={contactForm.email}
                  onChange={handleContactChange("email")}
                  data-testid="landing-contact-email-input"
                />
              </label>
              <label data-testid="landing-contact-company-label">
                Company
                <input
                  type="text"
                  name="company"
                  placeholder="Company name"
                  value={contactForm.company}
                  onChange={handleContactChange("company")}
                  data-testid="landing-contact-company-input"
                />
              </label>
              <label data-testid="landing-contact-team-label">
                Team size
                <select
                  name="team_size"
                  value={contactForm.team_size}
                  onChange={handleContactChange("team_size")}
                  data-testid="landing-contact-team-select"
                >
                  <option value="1-5">1–5</option>
                  <option value="6-25">6–25</option>
                  <option value="26-100">26–100</option>
                  <option value="100+">100+</option>
                </select>
              </label>
              <label className="full-width" data-testid="landing-contact-message-label">
                What indicators are you focused on?
                <textarea
                  name="message"
                  placeholder="Tell us about assets, horizons, and alert needs."
                  value={contactForm.message}
                  onChange={handleContactChange("message")}
                  data-testid="landing-contact-message-input"
                />
              </label>
              <div className="contact-actions" data-testid="landing-contact-actions">
                <span className="contact-note" data-testid="landing-contact-note">We respond within 1 business day.</span>
                <button
                  className="btn-primary"
                  type="submit"
                  data-testid="landing-contact-submit"
                  disabled={contactLoading}
                >
                  {contactLoading ? "Sending..." : "Request demo"}
                </button>
              </div>
              {contactStatus ? (
                <p
                  className={contactStatus.type === "error" ? "contact-note" : "contact-note"}
                  data-testid="landing-contact-status"
                >
                  {contactStatus.message}
                </p>
              ) : null}
            </form>
          </div>
        </section>

        <section id="faq" className="section" data-testid="landing-faq-section">
          <div className="section-title">
            <h2 data-testid="landing-faq-title">Questions?</h2>
            <p data-testid="landing-faq-subtitle">Here are the quick answers investors and analysts ask most.</p>
          </div>
          <div className="faq landing-reveal" data-testid="landing-faq-grid">
            {FAQ_ITEMS.map((item) => (
              <div className="faq-item" key={item.title} data-testid={`landing-faq-item-${item.title}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}> 
                <strong data-testid={`landing-faq-item-title-${item.title}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}>{item.title}</strong>
                <p data-testid={`landing-faq-item-text-${item.title}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}>{item.text}</p>
              </div>
            ))}
          </div>

          <div className="cta-band landing-reveal" data-testid="landing-cta-band">
            <h3 data-testid="landing-cta-title">Put the indicator stack to work.</h3>
            <p data-testid="landing-cta-subtitle">
              Start your 14‑day free trial today and see the inflection points before the market does.
            </p>
            <div data-testid="landing-cta-actions">
              <a className="btn-primary" href={`${PUBLIC_SITE_URL}/login`} data-testid="landing-cta-trial">
                Start free trial
              </a>
            </div>
            <div className="ticker" data-testid="landing-cta-tickers">
              {TICKERS.map((ticker) => (
                <span key={ticker} data-testid={`landing-cta-ticker-${ticker.toLowerCase()}`}>{ticker}</span>
              ))}
            </div>
          </div>
        </section>

        <section className="section" aria-label="Disclaimer" data-testid="landing-disclaimer-section">
          <div className="section-title">
            <h2 data-testid="landing-disclaimer-title">Disclaimer</h2>
            <p data-testid="landing-disclaimer-text">
              No information is financial advice. All information is for educational purposes only. Trading carries risk, and you are responsible for your own actions.
            </p>
          </div>
        </section>

        <footer data-testid="landing-footer">
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center" }} data-testid="landing-footer-links">
            <span data-testid="landing-footer-copy">© 2026 Bubble Radar. All rights reserved.</span>
            <a href={`${PUBLIC_SITE_URL}/privacy`} data-testid="landing-footer-privacy">Privacy Policy</a>
            <a href={`${PUBLIC_SITE_URL}/terms`} data-testid="landing-footer-terms">Terms &amp; Conditions</a>
          </div>
        </footer>
      </div>
    </div>
  );
}
